import React, { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { IdCard, Menu, X, Globe, Moon, Sun } from 'lucide-react';
import api from '@/lib/api';

export default function PublicLayout() {
  const { user } = useAuth();
  const { lang, switchLang, t } = useI18n();
  const { theme, toggleTheme } = useTheme();
  const [cms, setCms] = useState(null);
  const isDark = theme === 'dark';
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.get('/cms/settings').then(r => {
      setCms(r.data);
      if (r.data.site_title) document.title = r.data.site_title;
      // Apply favicon dynamically
      if (r.data.favicon_url) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.head.appendChild(link);
        }
        const faviconUrl = r.data.favicon_url.startsWith('http') ? r.data.favicon_url : `${process.env.REACT_APP_BACKEND_URL}${r.data.favicon_url}`;
        link.href = faviconUrl;
      }
    }).catch(() => {});
  }, []);

  const getDashboardPath = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin/dashboard';
    if (user.role === 'embassy') return '/embassy/dashboard';
    return '/citizen/dashboard';
  };

  return (
    <div className={`min-h-screen flex flex-col ${isDark ? 'bg-[#0B1120] text-gray-100' : 'bg-white text-[#0F172A]'}`} data-testid="public-layout">
      <header className={`sticky top-0 z-50 backdrop-blur border-b ${isDark ? 'bg-[#0B1120]/90 border-gray-800' : 'bg-white/90 border-border'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              {cms?.logo_url ? (
                <img src={cms.logo_url.startsWith('http') ? cms.logo_url : `${process.env.REACT_APP_BACKEND_URL}${cms.logo_url}`} alt="Logo" className="h-9 w-auto rounded-lg" />
              ) : (
                <div className="w-9 h-9 rounded-lg bg-[#007FFF] flex items-center justify-center">
                  <IdCard className="w-5 h-5 text-white" />
                </div>
              )}
              <span className={`font-semibold text-lg hidden sm:block ${isDark ? 'text-white' : ''}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                {cms?.site_title || "Diaspora RDC"}
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              <Link to="/" className={`text-sm font-medium transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-home">{t('nav.home')}</Link>
              <Link to="/#about" className={`text-sm font-medium transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-about">{t('nav.about')}</Link>
              <Link to="/verify" className={`text-sm font-medium transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-verify">{t('verify.title')}</Link>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg border transition-colors ${isDark ? 'border-gray-700 hover:bg-gray-800 text-yellow-400' : 'border-border hover:bg-muted text-gray-600'}`}
                data-testid="theme-toggle"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>

              {/* Language toggle */}
              <button
                onClick={() => switchLang(lang === 'fr' ? 'en' : 'fr')}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-sm font-medium transition-colors ${isDark ? 'border-gray-700 hover:bg-gray-800 text-gray-300' : 'border-border hover:bg-muted'}`}
                data-testid="lang-toggle"
              >
                <Globe className="w-3.5 h-3.5" />
                {lang === 'fr' ? 'EN' : 'FR'}
              </button>

              {user ? (
                <Link to={getDashboardPath()}>
                  <Button data-testid="nav-dashboard-btn">{t('nav.dashboard')}</Button>
                </Link>
              ) : (
                <div className="flex items-center gap-3">
                  <Link to="/login"><Button variant="ghost" className={isDark ? 'text-gray-300 hover:text-white hover:bg-gray-800' : ''} data-testid="nav-login-btn">{t('nav.login')}</Button></Link>
                  <Link to="/register"><Button data-testid="nav-register-btn">{t('nav.register')}</Button></Link>
                </div>
              )}
            </nav>

            <div className="md:hidden flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg ${isDark ? 'text-yellow-400' : 'text-gray-600'}`}
                data-testid="theme-toggle-mobile"
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button className="p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
                {mobileOpen ? <X className={`w-6 h-6 ${isDark ? 'text-white' : ''}`} /> : <Menu className={`w-6 h-6 ${isDark ? 'text-white' : ''}`} />}
              </button>
            </div>
          </div>

          {mobileOpen && (
            <div className="md:hidden pb-4 space-y-2">
              <Link to="/" className={`block px-3 py-2 text-sm ${isDark ? 'text-gray-300' : ''}`} onClick={() => setMobileOpen(false)}>{t('nav.home')}</Link>
              <Link to="/verify" className={`block px-3 py-2 text-sm ${isDark ? 'text-gray-300' : ''}`} onClick={() => setMobileOpen(false)}>{t('verify.title')}</Link>
              <button
                onClick={() => { switchLang(lang === 'fr' ? 'en' : 'fr'); setMobileOpen(false); }}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium ${isDark ? 'text-gray-300' : ''}`}
              >
                <Globe className="w-3.5 h-3.5" /> {lang === 'fr' ? 'English' : 'Francais'}
              </button>
              {user ? (
                <Link to={getDashboardPath()} onClick={() => setMobileOpen(false)}>
                  <Button className="w-full">{t('nav.dashboard')}</Button>
                </Link>
              ) : (
                <>
                  <Link to="/login" className="block" onClick={() => setMobileOpen(false)}>
                    <Button variant="ghost" className="w-full">{t('nav.login')}</Button>
                  </Link>
                  <Link to="/register" className="block" onClick={() => setMobileOpen(false)}>
                    <Button className="w-full">{t('nav.register')}</Button>
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="bg-[#0F172A] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                {cms?.logo_url ? (
                  <img src={cms.logo_url.startsWith('http') ? cms.logo_url : `${process.env.REACT_APP_BACKEND_URL}${cms.logo_url}`} alt="Logo" className="h-8 w-auto rounded-lg" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-[#007FFF] flex items-center justify-center">
                    <IdCard className="w-4 h-4 text-white" />
                  </div>
                )}
                <span className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {cms?.site_title || "Diaspora RDC"}
                </span>
              </div>
              <p className="text-sm text-gray-400">{cms?.site_description || "Plateforme officielle d'enregistrement"}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('nav.home')}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <Link to="/" className="block hover:text-white transition-colors">{t('nav.home')}</Link>
                <Link to="/register" className="block hover:text-white transition-colors">{t('nav.register')}</Link>
                <Link to="/login" className="block hover:text-white transition-colors">{t('nav.login')}</Link>
                <Link to="/verify" className="block hover:text-white transition-colors">{t('verify.title')}</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('cms.contact')}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                {cms?.contact_email && <p>{cms.contact_email}</p>}
                {cms?.contact_phone && <p>{cms.contact_phone}</p>}
              </div>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-gray-700 text-center text-sm text-gray-500">
            {cms?.footer_text || "Republique Democratique du Congo - Tous droits reserves"}
          </div>
        </div>
      </footer>
    </div>
  );
}
